<template>
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="700"
        :fullscreen="isMobile" persistent scrollable @keydown.esc="chiudi">
        <v-card>
            <v-card-title>
                {{ solaLettura ? 'Dettaglio incasso' : (form.id ? 'Modifica incasso' : 'Nuovo incasso') }}
                <v-chip v-if="solaLettura && form.stato" class="ml-2" size="small" :color="coloreRiga(form)"
                    variant="flat">
                    {{ etichettaRiga(form) }}
                </v-chip>
            </v-card-title>
            <v-card-text>
                <v-form ref="formIncasso" :disabled="solaLettura">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.tipoIncassoId" :items="tipiIncasso" item-title="nome"
                                item-value="id" label="Tipologia" :rules="[v => !!v || 'Campo obbligatorio']" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model.number="form.importo" type="number" step="0.01" label="Importo (€)"
                                :rules="[v => v !== null && v !== '' || 'Campo obbligatorio']" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.clienteId" :items="clienti" item-title="nome" item-value="id"
                                label="Cliente (opzionale)" clearable />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.pagatoreNote" label="Pagatore libero (se senza cliente)" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.dataScadenza" type="date" label="Data prevista incasso"
                                :rules="[v => !!v || 'Campo obbligatorio']" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.dataIncasso" type="date" label="Data incasso"
                                :disabled="solaLettura || form.stato !== 'Incassato'" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.idDocumentoInterno" label="ID documento interno (opzionale)"
                                :readonly="documentiReadonly && !form.id" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.numDocumentoInterno"
                                label="Numero documento interno (opzionale)"
                                :readonly="documentiReadonly && !form.id" />
                        </v-col>
                    </v-row>

                    <v-textarea v-model="form.descrizione" label="Descrizione" rows="2" />
                    <v-textarea v-model="form.noteIncasso" label="Note" rows="2" />

                    <v-checkbox v-model="form.ricorrente"
                        label="Rendi ricorrente (pianifica subito le prossime occorrenze, a data fissa)"
                        hint="Se non selezionato, alla chiusura verrà comunque generato automaticamente un solo prossimo incasso, calcolato dalla data di incasso effettiva."
                        persistent-hint />
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">{{ solaLettura ? 'Chiudi' : 'Annulla' }}</v-btn>
                <v-btn v-if="!solaLettura" color="primary" :loading="salvataggio" @click="salva">Salva</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { coloreRiga, etichettaRiga } from "@/constants/statoIncasso.js";
import storage from "@/services/storage.js";

export default {
    name: "DialogIncasso",

    props: {
        modelValue: { type: Boolean, default: false },
        incasso: { type: Object, default: null },
        // Riservato per un futuro utilizzo dal calendario unificato, come per
        // DialogPagamento (vedi Calendario.vue), non essendoci ancora integrazione
        // degli Incassi nel calendario.
        solaLettura: { type: Boolean, default: false },
        // Precompilazione per un NUOVO incasso (es. da un link con parametri in
        // query, vedi Incassi.vue): ignorata quando si modifica un incasso
        // esistente (il prop "incasso" ha sempre la precedenza).
        valoriIniziali: { type: Object, default: null },
        // Vedi commento analogo in DialogPagamento.vue.
        documentiReadonly: { type: Boolean, default: false },
    },
    emits: ["update:modelValue", "salvato"],

    data() {
        return {
            form: this.formVuoto(),
            salvataggio: false,
        };
    },

    computed: {
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        tipiIncasso() {
            return this.$store.getters["tipiIncasso/lista"];
        },
        clienti() {
            return this.$store.getters["clienti/lista"];
        },
    },

    watch: {
        modelValue(val) {
            if (val) this.inizializza();
        },

        // Vedi commento analogo in DialogPagamento.vue.
        form: {
            deep: true,
            handler(val) {
                if (this.modelValue && !this.solaLettura && !val.id) {
                    storage.setBozza("incasso", val);
                }
            },
        },
    },

    methods: {
        coloreRiga,
        etichettaRiga,

        formVuoto() {
            return {
                id: null,
                tipoIncassoId: null,
                clienteId: null,
                pagatoreNote: "",
                importo: null,
                stato: "DaIncassare",
                dataScadenza: new Date().toISOString().substr(0, 10),
                dataIncasso: null,
                descrizione: "",
                noteIncasso: "",
                ricorrente: false,
                idDocumentoInterno: "",
                numDocumentoInterno: "",
            };
        },

        async inizializza() {
            this.form = this.incasso
                ? {
                    id: this.incasso.id,
                    tipoIncassoId: this.incasso.tipoIncassoId,
                    clienteId: this.incasso.clienteId,
                    pagatoreNote: this.incasso.pagatoreNote,
                    importo: this.incasso.importo,
                    stato: this.incasso.stato,
                    dataScadenza: this.toDateInput(this.incasso.dataScadenza),
                    dataIncasso: this.toDateInput(this.incasso.dataIncasso),
                    descrizione: this.incasso.descrizione,
                    noteIncasso: this.incasso.noteIncasso,
                    ricorrente: this.incasso.ricorrente ?? false,
                    idDocumentoInterno: this.incasso.idDocumentoInterno || "",
                    numDocumentoInterno: this.incasso.numDocumentoInterno || "",
                }
                : { ...this.formVuoto(), ...this.valoriIniziali };

            await Promise.all([
                this.$store.dispatch("tipiIncasso/carica"),
                this.$store.dispatch("clienti/carica"),
            ]);
        },

        toDateInput(data) {
            return data ? new Date(data).toISOString().substr(0, 10) : null;
        },

        async salva() {
            if (!this.$refs.formIncasso.validate()) return;

            this.salvataggio = true;
            try {
                const { id, ...dati } = this.form;
                delete dati.stato;
                await this.$store.dispatch("incassi/salva", { id, dati });
                storage.clearBozza("incasso");
                this.$emit("salvato");
                this.chiudi();
            } catch (error) {
                console.log(error);
            }
            this.salvataggio = false;
        },

        chiudi() {
            if (!this.form.id) storage.clearBozza("incasso");
            this.$emit("update:modelValue", false);
        },
    },
};
</script>
